import { BooleanOption, Column, ColumnKeys, GridActions, IntegrityMonitorConfigurationStatus } from '../types';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { html } from 'lit';
import { ColumnDetails } from '../../../../grid/types/common';
import { getIntegrityMonitorTypeValues } from '../../../common/utils/types';
import { ButtonColors, ButtonHeights, ButtonTypes } from '../../../../ui/gzButton/types';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { InlineRowActionsWebComponent } from '../../../../grid/uiElements/inlineRowActions';
import { WEB_COMPONENT_INLINE_ROW_ACTIONS } from '../../../../webcomponents.constants';
import { DefaultSmartViewId } from '../../integrityMonitorRulesSmartViews/types';
import { Rights } from '@nirvana/shared/constants';

export function ruleNameRenderer(rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) {
  const cellValue = rowData.item[columnDetails.key];

  const button = document.createElement('gz-button') as GzButtonWebComponent;
  button.type = ButtonTypes.QUIET;
  button.buttonHeight = ButtonHeights.SMALL;
  button.innerText = cellValue;
  button.color = ButtonColors.PRIMARY;
  button.lowercase = true;

  button.addEventListener('click', this.onClickOnRuleName.bind(this, rowData.item));

  return button;
}

export function entityTypeRenderer(rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) {
  const cellValue = rowData.item['entities'][0][columnDetails.key];
  let label: string;

  if (isEmpty(cellValue)) {
    return '-';
  }

  // Check if the rule monitors multiple entities or just one
  if (rowData.item['entities'].filter(element => cellValue !== element.entityType).length > 0) {
    label = this.dependencies.translateFn('Multiple');
  } else {
    label = getIntegrityMonitorTypeValues(this.dependencies.translateFn).find(
      element => element.value === cellValue
    )?.label;
  }

  return html`<span class="cell-label label">${label}</span>`;
}

export function booleanRenderer(
  options: { positive: string; negative: string },
  rowData,
  columnDetails: ColumnDetails<Column, ColumnKeys>
) {
  const cellValue = rowData.item[columnDetails.key];
  let label: string;

  if (isEmpty(cellValue)) {
    return '-';
  }

  label = +cellValue === BooleanOption.POSITIVE ? options.positive : options.negative;

  return html`<span class="cell-label label">${label}</span>`;
}

export function statusRenderer(rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) {
  const cellValue = rowData.item[columnDetails.key];
  let label: string;

  if (!Array.isArray(cellValue)) {
    return '-';
  }

  label = cellValue.length ? this.dependencies.translateFn('Assigned') : this.dependencies.translateFn('Unassigned');

  return html`<span class="cell-label label">${label}</span>`;
}

export function configurationStatusRenderer(rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) {
  const cellValue = rowData.item[columnDetails.key] ?? 0;

  switch (cellValue) {
    case IntegrityMonitorConfigurationStatus.STATUS_UNCONFIGURED:
      return html` <gz-tooltip
        tooltiptext="${this.translations.defaultRuleConfiguration.needsConfiguration}"
        id="needs-config-tooltip"
      >
        <span part="im-icon" class="cell-label ds-icon-notification_warning notice"></span>
      </gz-tooltip>`;
    case IntegrityMonitorConfigurationStatus.STATUS_SOURCE_DELETED:
      return html` <gz-tooltip
        tooltiptext="${this.translations.defaultRuleConfiguration.sourceDeleted}"
        id="source-deleted-tooltip"
      >
        <span part="im-icon" class="cell-label ds-icon-notification_warning danger"></span>
      </gz-tooltip>`;
    case IntegrityMonitorConfigurationStatus.STATUS_SOURCE_UPDATED:
      return html` <gz-tooltip
        tooltiptext="${this.translations.defaultRuleConfiguration.sourceUpdated}"
        id="source-updated-tooltip"
      >
        <span part="im-icon" class="cell-label ds-icon-notification_warning warning"></span>
      </gz-tooltip>`;
    case IntegrityMonitorConfigurationStatus.STATUS_CONFIGURED_OK:
    default:
      return '';
  }
}

export function inlineRowActionsRenderer(rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) {
  if (!Application.Main.hasAllRights([Rights.MANAGE_POLICIES_WRITE])) {
    return ``;
  }

  if (!this.dependencies.canModifyRulesAndRuleSets()) {
    return ``;
  }

  const menuBtn = window.document.createElement(WEB_COMPONENT_INLINE_ROW_ACTIONS) as InlineRowActionsWebComponent;
  const actionsArray = [
    {
      title: this.dependencies.translateFn('Correct'),
      value: GridActions.CORRECT_RULE,
      customClass: 'warning',
      disabled: rowData.item.configurationStatus === IntegrityMonitorConfigurationStatus.STATUS_CONFIGURED_OK,
    },
    {
      title: this.dependencies.translateFn('Delete'),
      value: GridActions.DELETE_RULE,
      customClass: 'danger',
      disabled: rowData.item.isDefaultRule,
    },
  ];

  switch (this.dom.$gridView.viewId) {
    case DefaultSmartViewId.DEFAULT_OS_RULES:
    case DefaultSmartViewId.DEFAULT_APPLICATION_RULES:
      menuBtn.inlineActions = [actionsArray[0]];
      break;
    case DefaultSmartViewId.CUSTOM_RULES:
      menuBtn.inlineActions = [actionsArray[1]];
      break;
    case DefaultSmartViewId.ALL_RULES:
    default:
      menuBtn.inlineActions = actionsArray;
  }

  menuBtn.itemData = rowData;
  menuBtn.classList.add(`row-${rowData.index + 2}`);

  return menuBtn;
}
